import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import PropTypes from 'prop-types';

import { chartJsDefaultTooltip } from 'helpers/chartjs-utils';
import { getColor, rgbaColor } from 'helpers/utils';
import isEmpty from 'utils/isEmpty';
import pluck from 'utils/array/pluck';
import randomInt from 'utils/number/randomInt';

/**
 * 대시보드 > 추이 대시보드 - 차트
 */
const Chart = ({ inform, type }) => {
  /* reference */
  const chart = useRef(null);

  /* state */
  // 데이터
  const [data, setData] = useState({});
  // 종류 목록
  const [keys, setKeys] = useState([]);
  // 옵션
  const [options, setOptions] = useState({});

  /* watch */
  useEffect(() => {
    if (isEmpty(inform)) {
      return;
    }

    const keys = [...new Set(pluck(inform.datasets, 'key'))];
    const { length } = keys;
    const division = 0xffffff / length;
    const borderColors = inform.datasets.reduce((result, value, index) => {
      let color = '';

      value = index % length;

      do {
        color = `#${randomInt(division * value, division * (value + 1))
          .toString(16)
          .padStart(6, 0)}`;
      } while (result.includes(color));

      return [...result, color];
    }, []);
    const options = {
      scales: {
        x: { grid: { color: rgbaColor(getColor('black'), 0.1) } },
        y: {
          grid: { color: rgbaColor(getColor('black'), 0.1) },
          beginAtZero: true,
          position: 'left'
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: chartJsDefaultTooltip()
      }
    };

    // 원료 가격 추이인 경우 대두박은 오른쪽 차트 가격으로 표시
    if (type === 0) {
      options.scales.y1 = {
        grid: {
          color: rgbaColor(getColor('black'), 0.1),
          drawOnChartArea: false
        },
        beginAtZero: true,
        position: 'right'
      };
    }

    setData({
      ...inform,
      datasets: inform.datasets.map((value, index) => ({
        ...value,
        borderColor: borderColors[index],
        borderWidth: 2,
        fill: false,
        tension: 0.2,
        yAxisID: value.key === '대두박' ? 'y1' : 'y'
      }))
    });
    setKeys(keys);
    setOptions(options);
  }, [inform]);
  useEffect(() => {
    setData({});
  }, [type]);

  /* event */
  // 라벨 클릭 시
  const onLabelClick = ({ target }, borderColor) => {
    const { datasetIndex, hidden } =
      chart.current.options.plugins.legend.labels
        .generateLabels(chart.current)
        .find(({ strokeStyle }) => borderColor === strokeStyle) ?? {};

    // 해당 라벨의 데이터 표시/숨김
    target.style.textDecoration = !hidden ? 'line-through' : '';

    chart.current.setDatasetVisibility(
      datasetIndex,
      !chart.current.isDatasetVisible(datasetIndex)
    );
    chart.current.update();
  };

  /* view */
  return (
    <>
      {!isEmpty(data) && (
        <>
          <div>
            {type === 0 ? (
              keys.map(value => (
                <ul
                  key={`keys.${value}`}
                  className="d-flex justify-content-end mb-1"
                >
                  <li className="d-flex fs--1 lh-1 fw-bold align-items-center">
                    &lt;{value}&gt;
                  </li>
                  {data.datasets
                    .filter(({ key }) => key === value)
                    .map(({ label, borderColor }, index) => (
                      <li
                        key={`datasets${index}`}
                        className="d-flex fs--1 lh-1 align-items-center ms-3 cursor-pointer"
                        onClick={event => onLabelClick(event, borderColor)}
                      >
                        <span
                          className="d-block me-2"
                          style={{
                            backgroundColor: borderColor,
                            width: '10px',
                            height: '10px'
                          }}
                        />
                        {label}
                      </li>
                    ))}
                </ul>
              ))
            ) : (
              <ul className="d-flex justify-content-end mb-1">
                {data.datasets.map(({ label, borderColor }, index) => (
                  <li
                    key={`datasets${index}`}
                    className="d-flex fs--1 lh-1 align-items-center ms-3 cursor-pointer"
                    onClick={event => onLabelClick(event, borderColor)}
                  >
                    <span
                      className="d-block me-2"
                      style={{
                        backgroundColor: borderColor,
                        width: '10px',
                        height: '10px'
                      }}
                    />
                    {label}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Line ref={chart} data={data} options={options} height={100} />
        </>
      )}
    </>
  );
};

Chart.propTypes = {
  // 추이 정보
  inform: PropTypes.shape({
    datasets: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          data: PropTypes.arrayOf(PropTypes.number),
          key: PropTypes.string,
          label: PropTypes.string
        }),
        PropTypes.shape({
          data: PropTypes.arrayOf(PropTypes.number),
          label: PropTypes.string
        })
      ])
    ),
    labels: PropTypes.arrayOf(PropTypes.string)
  }),

  // 구분 (0: 원료 가격 추이, 1: 제품 생산량 추이, 2: 원재료비 추이)
  type: PropTypes.number
};
Chart.defaultProps = {
  inform: {},
  type: 0
};

export default Chart;
